import PopClipsCard from '@/components/common/PopClipsCard';
import { popclipsRoutes } from '@/constants/routes.constants';
import { analyticsEventStore, popclipsStore, shopStore } from '@/stores';
import {
    BlockStack,
    Box,
    Checkbox,
    Divider,
    InlineStack,
    Spinner,
    Text,
} from '@shopify/polaris';
import { useEffect, useState } from 'react';
import { BaseLayout } from '../../v2/pages/Layout/BaseLayout';
import { BYPASS_GATING_FOR_DOMAINS } from '@/constants/shopify.constants';
import { VfsButton } from '@/components/common/v1';
import PopclipsMiniplayerPositioning from '@/components/popclips/popclips-grid-components/PopclipsMiniplayerPositioning';
import { useSnapshot } from 'valtio';

type PopclipsSettings = {
    hidePopClipsOnPDP: boolean;
    showViewsOnPopClips: boolean;
    showLikesOnPopClips: boolean;
    enableShopFeaturesBuyBtn: boolean;
};

let isFirstLoad = true;
let previousSettingsCache = {} as PopclipsSettings;

const PopClipSettingsPage = () => {
    const [apiLoading, setApiLoading] = useState(false);
    const [shouldShowViewToggle, setShouldShowViewToggle] = useState(false);
    const [shouldShowLikeToggle, setShouldShowLikeToggle] = useState(false);
    const [settings, setSettings] = useState({
        hidePopClipsOnPDP: false,
        showViewsOnPopClips: false,
        showLikesOnPopClips: false,
        enableShopFeaturesBuyBtn: false,
    });
    const { showMiniplayerPositioningModal } = useSnapshot(popclipsStore)

    const saveSettings = async () => {
        shopify.saveBar.hide('shopify-save-bar');
        setApiLoading(true);

        await fetch('/api/shopify/shop/popclips/settings', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(settings),
        });
        const payload = {
            previous_hide_popclips_on_pdp: previousSettingsCache.hidePopClipsOnPDP,
            new_hide_popclips_on_pdp: settings.hidePopClipsOnPDP,
            
            previous_enable_shop_features_buy_btn: previousSettingsCache.enableShopFeaturesBuyBtn,
            new_enable_shop_features_buy_btn:  settings.enableShopFeaturesBuyBtn,
        }
        if(shouldShowViewToggle){
            Object.assign(payload, {
                previous_show_views_on_popclips: previousSettingsCache.showViewsOnPopClips,
                new_show_views_on_popclips: settings.showViewsOnPopClips,
            })
        }
        if(shouldShowLikeToggle) {
            Object.assign(payload, {
                previous_show_likes_on_popclips: previousSettingsCache.showLikesOnPopClips,
                new_show_likes_on_popclips: settings.showLikesOnPopClips
            })
        }
        await analyticsEventStore.popclipSettingChangedEvent(payload)

        previousSettingsCache = settings;

        shopify.toast.show('Settings saved successfully.', {
            isError: false,
            duration: 5000,
        });

        setApiLoading(false);
    };

    useEffect(() => {
        (async () => {
            setApiLoading(true);
            const res = await fetch('/api/shopify/shop/popclips/settings');
            const result = await res.json();
            setApiLoading(false);

            if (result && result.data) {
                const { shopifyDomain, ...rest } = result.data;
                previousSettingsCache = rest;
                setSettings(rest);

                setTimeout(() => (isFirstLoad = false), 300);
            }
        })();
    }, []);

    useEffect(() => {
        const addonPrices = shopStore.getActivePlanAddonPrices(
            'popclips',
            true,
        );
        const maxPrice = Math.max(...addonPrices);

        if (BYPASS_GATING_FOR_DOMAINS.includes(shopStore.shopifyDomain)) {
            setShouldShowViewToggle(true);
            setShouldShowLikeToggle(true);
        } else {
            setShouldShowViewToggle(maxPrice >= 29.99);
            setShouldShowLikeToggle(maxPrice >= 149.99);
        }
    }, [shopStore.subscription]);

    const discardUnsavedChanges = () => {
        setSettings(previousSettingsCache);
        shopify.saveBar.hide('shopify-save-bar');
    };

    useEffect(() => {
        if (isFirstLoad) return;
        let valueIsChanged = 0;

        if (
            settings.hidePopClipsOnPDP !==
            previousSettingsCache.hidePopClipsOnPDP
        )
            valueIsChanged++;
        if (
            settings.showViewsOnPopClips !==
            previousSettingsCache.showViewsOnPopClips
        )
            valueIsChanged++;
        if (
            settings.showLikesOnPopClips !==
            previousSettingsCache.showLikesOnPopClips
        )
            valueIsChanged++;
        if (
            settings.enableShopFeaturesBuyBtn !==
            previousSettingsCache.enableShopFeaturesBuyBtn
        )
            valueIsChanged++;

        if (valueIsChanged > 0) {
            shopify.saveBar.show('shopify-save-bar');
        } else {
            shopify.saveBar.hide('shopify-save-bar');
        }
    }, [settings]);

    return (
        <BaseLayout
            title="Popclips Settings & Configurations"
            backToLink={
                !shopStore.getStartedStatus.events.STEP_ADD_YOUR_FIRST_POPCLIP
                    ? popclipsRoutes.create
                    : popclipsRoutes.list
            }
            saveBarPrimaryAction={saveSettings}
            saveBarSecondaryAction={discardUnsavedChanges}
        >
            <PopClipsCard>
                <Box padding="400">
                    <BlockStack gap="400">
                        <Box paddingBlockEnd="400">
                            <BlockStack gap="200">
                                <Text as="p" variant="headingMd">
                                    Manage how your Popclips will be displayed to
                                    your customers.
                                </Text>
                                <Text as="p" variant="bodyMd">
                                    Configure how your Popclips will appear to
                                    your customers.
                                </Text>
                            </BlockStack>
                        </Box>
                        <BlockStack gap="300">
                            <InlineStack blockAlign="center" gap="400">
                                <Text as="p" variant="bodyMd">Customize placement of the Popclips Miniplayer</Text>
                                <VfsButton variant='secondary' padding='8px 12px' onClick={() => popclipsStore. showMiniplayerPositioningModal = true}>Adjust Position</VfsButton>
                            </InlineStack>
                            <Divider />
                            <InlineStack align="space-between">
                                <Text as="p" variant="bodyMd">
                                    Hide Popclips miniplayer on all product
                                    Detail Pages
                                </Text>
                                <div>
                                    {apiLoading ? (
                                        <Spinner size="small" />
                                    ) : (
                                        <Checkbox
                                            label=""
                                            disabled={apiLoading}
                                            checked={settings.hidePopClipsOnPDP}
                                            onChange={(checked) => {
                                                setSettings({
                                                    ...settings,
                                                    ...{
                                                        hidePopClipsOnPDP:
                                                            checked,
                                                    },
                                                });
                                            }}
                                        />
                                    )}
                                </div>
                            </InlineStack>
                            <Divider />
                        </BlockStack>
                        {shouldShowViewToggle && (
                            <BlockStack gap="300">
                                <InlineStack align="space-between">
                                    <Text as="p" variant="bodyMd">
                                        Show number of views on your Popclips
                                    </Text>
                                    <div>
                                        {apiLoading ? (
                                            <Spinner size="small" />
                                        ) : (
                                            <Checkbox
                                                label=""
                                                disabled={apiLoading}
                                                checked={
                                                    settings.showViewsOnPopClips
                                                }
                                                onChange={(checked) => {
                                                    setSettings({
                                                        ...settings,
                                                        ...{
                                                            showViewsOnPopClips:
                                                                checked,
                                                        },
                                                    });
                                                }}
                                            />
                                        )}
                                    </div>
                                </InlineStack>
                                <Divider />
                            </BlockStack>
                        )}
                        {shouldShowLikeToggle && (
                            <BlockStack gap="300">
                                <InlineStack align="space-between">
                                    <Text as="p" variant="bodyMd">
                                        Show number of likes on your Popclips
                                    </Text>
                                    <div>
                                        {apiLoading ? (
                                            <Spinner size="small" />
                                        ) : (
                                            <Checkbox
                                                label=""
                                                disabled={apiLoading}
                                                checked={
                                                    settings.showLikesOnPopClips
                                                }
                                                onChange={(checked) => {
                                                    setSettings({
                                                        ...settings,
                                                        ...{
                                                            showLikesOnPopClips:
                                                                checked,
                                                        },
                                                    });
                                                }}
                                            />
                                        )}
                                    </div>
                                </InlineStack>
                                <Divider />
                            </BlockStack>
                        )}
                        <Box>
                            <InlineStack align="space-between">
                                <Text as="p" variant="bodyMd">
                                    Enable shop feature on your Popclips feed on
                                    your website (Buy button with checkout
                                    experience directly from the website
                                    Popclips feed)
                                </Text>
                                <div>
                                    {apiLoading ? (
                                        <Spinner size="small" />
                                    ) : (
                                        <Checkbox
                                            label=""
                                            disabled={apiLoading}
                                            checked={
                                                settings.enableShopFeaturesBuyBtn
                                            }
                                            onChange={(checked) => {
                                                setSettings({
                                                    ...settings,
                                                    ...{
                                                        enableShopFeaturesBuyBtn:
                                                            checked,
                                                    },
                                                });
                                            }}
                                        />
                                    )}
                                </div>
                            </InlineStack>
                        </Box>
                    </BlockStack>
                </Box>
            </PopClipsCard>

            {showMiniplayerPositioningModal && 
                <PopclipsMiniplayerPositioning
                    type="MINI_PLAYER"
                    showModal={showMiniplayerPositioningModal}
                    onCloseModal={() => popclipsStore.showMiniplayerPositioningModal =false}
                />
            }
        </BaseLayout>
    );
};

export default PopClipSettingsPage;
